// use client: search/filter/pagination state and router.push handlers
'use client'

import '@/styles/page.css'
import './guides.css'

import type { Article, ArticleTag, GuideCategory as GuideCategoryItem } from './shared/types'

import { useRef, useState } from 'react'
import { Col, Row, Stack } from 'react-bootstrap'
import { isEmpty } from 'lodash'
import { useParams, useRouter } from 'next/navigation'

import guideMain from '@/assets/images/guides/guide-person.webp'
import SearchIcon from '@/assets/images/search.svg'
import Breadcrumbs from '@/components/breadcrumbs'
import { Maintenance } from '@/components/maintenance'
import PaginationComponent from '@/components/pagination/PaginationComponent'
import { useIsMobile } from '@/hooks/useIsMobile'
import { convertMonthEnglishFormat, parseCmsHtml } from '@/utils'
import { ArticleCard } from './shared/ArticleCard'
import { QuickFilterSidebar } from './shared/QuickFilterSidebar'
import { useArticleFilters } from './shared/useArticleFilters'

function GuideCategory({
  initialData,
  initialCategory = null,
  initialTags = null
}: {
  initialData?: Article[] | null
  initialCategory?: GuideCategoryItem | null
  initialTags?: ArticleTag[] | null
}) {
  const { category } = useParams()
  const categoryStr = Array.isArray(category) ? category[0] : (category ?? '')
  // Tags come from the build-time snapshot only. A Strapi publish rebuilds and
  // redeploys the site, so there is nothing to revalidate on mount.
  // See tests/no-client-cms-fetch.spec.ts.
  const articleTags: ArticleTag[] = initialTags ?? []
  const [sortActive, setSortActive] = useState(false)
  const [topicActive, setTopicActive] = useState(false)

  const {
    articleList,
    guidesList,
    pagination,
    setPagination,
    search,
    setSearch,
    // NOTE: this page's sort radios are uncontrolled — it only ever writes the
    // sort, never reads it back (unlike Guides.tsx). Kept as-is; see the
    // copy-paste divergence recorded in the PR follow-ups.
    setSelectedSort,
    selectedTopics,
    setSelectedTopics,
    isData
  } = useArticleFilters({
    categorySlug: categoryStr,
    initial: { articles: initialData },
    // The server already seeded page 1 — don't refetch it on mount (matches
    // Guides.tsx; without this the hook fetched the same first page again).
    skipInitialFetch: initialData != null
  })

  const isMobile = useIsMobile({ maxWidth: '360px' })
  const isTabScreen = useIsMobile({ maxWidth: '576px' })
  const isBigScreen = useIsMobile({ maxWidth: '768px' })

  const router = useRouter()
  const ref = useRef(null)

  return isData ? (
    <div id="guides" className="container page">
      {/* Banner Section */}
      <Breadcrumbs />
      <div
        style={{
          marginBottom: '80px',
          borderBottom: '1px solid #0000451A',
          paddingBottom: '80px'
        }}
      >
        <h2 className="page-title">{initialCategory?.title}</h2>
        {/* div, not p: CMS rich text contains its own <p>, and a <p> inside a <p> is
            auto-closed by the HTML parser -> hydration mismatch. mb-3 restores the
            <p> reboot margin this div wrapper replaced. */}
        <div className="tagline mb-3">
          {initialCategory?.description && parseCmsHtml(initialCategory.description)}
        </div>

        <Row className="justify-content-between flex-nowrap">
          {guidesList[0] && (
            <Col
              style={{
                flex: '1'
              }}
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div
                className="guide-main-card"
                style={{
                  backgroundImage: isTabScreen
                    ? 'none'
                    : `url(${
                        guidesList[0]?.image?.image || guidesList[0]?.image?.imageLink || ''
                      })`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: isTabScreen ? '500px' : '80vh'
                  // minHeight: "100%",
                }}
                onClick={() => {
                  router.push(
                    `/personal/guides/${guidesList[0]?.guide_category?.slug ?? categoryStr}/${guidesList[0]?.slug}/`
                  )
                }}
              >
                <div
                  className="w-100"
                  style={{
                    height: isMobile ? '60%' : '65%',
                    overflow: 'hidden',
                    display: isTabScreen ? 'flex' : 'none',
                    borderRadius: isTabScreen ? '20px' : 'none'
                  }}
                >
                  <img
                    alt={guidesList[0]?.image?.altText}
                    src={
                      guidesList &&
                      (guidesList[0]?.image?.image?.url || guidesList[0]?.image?.imageLink)
                    }
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      display: 'block'
                    }}
                  />
                </div>
                <div className="w-100 guide-main-card-content" style={{ textOverflow: 'ellipsis' }}>
                  <p className="dateTag">
                    {guidesList[0]?.date && convertMonthEnglishFormat(guidesList[0]?.date)}
                  </p>

                  <h4 className="title">
                    {guidesList && isMobile
                      ? guidesList[0]?.title.slice(0, 35) + '...'
                      : guidesList[0]?.title.length < 80
                        ? guidesList[0]?.title
                        : guidesList[0]?.title.slice(0, 80) + '...'}
                  </h4>
                  <p>
                    {guidesList && isMobile
                      ? guidesList[0]?.subtitle.slice(0, 100) + '...'
                      : guidesList[0]?.subtitle.length < 150
                        ? guidesList[0]?.subtitle
                        : isBigScreen
                          ? guidesList[0]?.subtitle.slice(0, 120) + '...'
                          : guidesList[0]?.subtitle}
                  </p>
                  <div className="d-none d-sm-block">
                    {guidesList && (guidesList[0]?.article_tags?.length ?? 0) > 0 && (
                      <Stack direction="horizontal" gap={3}>
                        {guidesList[0]?.article_tags?.map(item => {
                          return (
                            <div key={item.title} className="stack-tag">
                              {item.title}
                            </div>
                          )
                        })}
                      </Stack>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Latest Articles */}
      <div data-aos="fade-up" data-aos-delay="150" ref={ref}>
        <h2 className="page-title">Latest Articles</h2>
        <Row className="my-5 d-flex">
          <Col
            lg={3}
            className="guide-quick-filter-container"
            data-aos="fade-right"
            data-aos-delay="150"
          >
            <QuickFilterSidebar
              articleTags={articleTags}
              search={search}
              setSearch={setSearch}
              setSelectedSort={setSelectedSort}
              selectedTopics={selectedTopics}
              setSelectedTopics={setSelectedTopics}
              sortActive={sortActive}
              setSortActive={setSortActive}
              topicActive={topicActive}
              setTopicActive={setTopicActive}
            />
          </Col>
          <Col
            lg={9}
            className="guide-latest-article-container"
            data-aos="fade-left"
            data-aos-delay="150"
          >
            {!isEmpty(articleList) ? (
              articleList.map(item => (
                <ArticleCard
                  key={item.slug}
                  item={item}
                  isMobile={isMobile}
                  isTabScreen={isTabScreen}
                  imageFallbackSrc={guideMain.src}
                  onClick={() =>
                    router.push(
                      `/personal/guides/${item?.guide_category?.slug ?? categoryStr}/${item.slug}/`
                    )
                  }
                />
              ))
            ) : (
              <div className="mx-2">No Data Available</div>
            )}

            {pagination.pageCount > 1 && (
              <PaginationComponent
                scrollTargetRef={ref}
                pagination={pagination}
                setPagination={setPagination}
              />
            )}
          </Col>
        </Row>
      </div>
    </div>
  ) : (
    <Maintenance isComingSoon={true} />
  )
}

export default GuideCategory
